import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { int, useQueryParams } from '../../hooks/useQueryParams';
import { dateOnly } from '../../services/format';
import { InvoiceAmounts, IssueBadge, KindBadge, invoiceNumber } from '../../components/invoice';
import { Alert, Badge, Button, Cell, Field, Filters, Input, PageTitle, Pager, Row, Select, Table, statusTone } from '../../components/ui';

const PAGE_SIZE = 50;

export function InvoicesList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  const status = get('status');
  const organizationId = get('organization_id');
  const { data, error, loading } = useAsync(() => api.invoices({ status: status || undefined, organization_id: int(organizationId), page, page_size: PAGE_SIZE }), [status, organizationId, page]);

  return (
    <div>
      <PageTitle title={t('invoices.title')} subtitle={data ? t('common.count', { count: data.total }) : undefined} actions={<Button variant="primary" onClick={() => navigate('/invoices/new')}>{t('invoices.new')}</Button>} />
      <Filters onReset={reset}>
        <Field label={t('invoices.statusLabel')}>
          <Select value={status} onChange={(e) => set({ status: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {(['draft', 'sent', 'paid', 'void'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`invoices.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('users.organizationId')} className="w-32">
          <Input type="number" value={organizationId} onChange={(e) => set({ organization_id: e.target.value })} />
        </Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      <Table head={[t('invoices.number'), t('invoices.kind.label'), t('invoices.organization'), t('invoices.statusLabel'), t('invoices.issue.label'), t('invoices.amountExcl'), t('invoices.vat'), t('invoices.total'), t('invoices.issuedAt'), t('invoices.dueAt'), t('invoices.pdf')]} empty={loading ? t('common.loading') : t('common.empty')}>
        {(data?.items ?? []).map((i) => (
          <Row key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}>
            <Cell mono className={i.number ? '' : 'text-faint'}>{invoiceNumber(i, t)}</Cell>
            <Cell>
              <KindBadge invoice={i} />
            </Cell>
            <Cell>{i.organization_name}</Cell>
            <Cell>
              <Badge tone={statusTone(i.status)}>{t(`invoices.status.${i.status}`)}</Badge>
            </Cell>
            <Cell>
              <IssueBadge invoice={i} />
            </Cell>
            <InvoiceAmounts invoice={i} />
            <Cell mono>{dateOnly(i.issued_at, i18n.language)}</Cell>
            <Cell mono>{dateOnly(i.due_at, i18n.language)}</Cell>
            <Cell>{i.has_pdf ? t('common.yes') : t('common.no')}</Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
    </div>
  );
}
